import { LOW_STORE_OC_THRESHOLD, isLowStoreOc } from '../../lib/oc';
import type { PubCustomer } from './types';

export { LOW_STORE_OC_THRESHOLD, isLowStoreOc };


export const QUICK_BUY_IN_AMOUNTS = [10_000, 20_000, 30_000, 50_000, 100_000] as const;

/** 입력 문자열을 양의 정수로 바꾼다. 쉼표·공백은 허용하고, 그 밖의 값은 null. */
export function parsePositiveInteger(value: string): number | null {
  const normalized = value.replace(/[,\s]/g, '');
  if (!/^\d+$/.test(normalized)) return null;
  const parsed = Number(normalized);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null;
}

/** 서버(workers/api pub.ts)와 같은 규칙: 포인트를 먼저 쓰고, 부족분은 1,000포인트당 원 환율로 현금 환산(올림). */
export function estimateBuyIn(amount: number, availablePoints: number, krwPer1000Points: number) {
  const pointsUsed = Math.min(Math.max(availablePoints, 0), amount);
  return { pointsUsed, cashKrw: Math.ceil(((amount - pointsUsed) * krwPer1000Points) / 1000) };
}

export function customerLabel(customer: Pick<PubCustomer, 'nickname' | 'internal_login_id' | 'id'>): string {
  return customer.nickname?.trim() || customer.internal_login_id || customer.id.slice(0, 8);
}

/** 서버가 닉네임을 주면 그것을, 아니면 고객 목록에서 찾은 값을, 둘 다 없으면 ID 앞 8자를 보여 준다. */
export function resolveCustomerName(
  row: { customer_id: string; customer_nickname?: string | null },
  customers: readonly PubCustomer[],
): string {
  const direct = row.customer_nickname?.trim();
  if (direct) return direct;
  const found = customers.find((customer) => customer.id === row.customer_id);
  return found ? customerLabel(found) : row.customer_id.slice(0, 8);
}

/** 고객 검색은 서버에 검색 파라미터가 없어 클라이언트에서 닉네임·로그인 ID로 거른다. */
export function filterCustomers(customers: readonly PubCustomer[], keyword: string): PubCustomer[] {
  const needle = keyword.trim().toLowerCase();
  if (!needle) return [...customers];
  return customers.filter((customer) =>
    [customer.nickname, customer.internal_login_id].some((value) => value?.toLowerCase().includes(needle)),
  );
}

export interface PrizeLine {
  customerId: string;
  amount: string;
}

/**
 * 토너먼트 결과 입력 행을 서버 형식({ [customer_id]: amount })으로 바꾼다.
 * 오류가 있으면 사용자에게 보여 줄 메시지를 error로 돌려준다.
 */
export function buildPrizeMap(lines: readonly PrizeLine[]): { prizes: Record<string, number>; error: string | null } {
  const prizes: Record<string, number> = {};
  const used = lines.filter((line) => line.customerId || line.amount.trim());
  if (used.length === 0) return { prizes, error: '결과를 한 명 이상 입력하세요.' };
  if (used.length > 100) return { prizes, error: '결과는 최대 100명까지 입력할 수 있습니다.' };
  for (const [index, line] of used.entries()) {
    if (!line.customerId) return { prizes, error: `${index + 1}번째 줄의 고객을 선택하세요.` };
    const amount = parsePositiveInteger(line.amount);
    if (amount === null) return { prizes, error: `${index + 1}번째 줄의 상금은 양의 정수여야 합니다.` };
    if (line.customerId in prizes) return { prizes, error: '같은 고객이 두 번 입력되었습니다.' };
    prizes[line.customerId] = amount;
  }
  return { prizes, error: null };
}

/** datetime-local 값("2026-10-30T19:00")을 서버가 받는 ISO 문자열로 바꾼다. */
export function toIsoFromLocalInput(value: string): string | null {
  if (!value) return null;
  const time = new Date(value);
  return Number.isNaN(time.getTime()) ? null : time.toISOString();
}

export const TOURNAMENT_STATUS_LABEL: Record<string, string> = {
  in_progress: '진행 중',
  completed: '종료',
  modified: '결과 수정됨',
};

export const AUDIT_ACTION_LABEL: Record<string, string> = {
  process_buy_in: '바이인 처리',
  award_prize: '상금 지급',
  grant_bonus: '보너스 지급',
  cancel_bonus: '보너스 취소',
  create_tournament: '토너먼트 생성',
  modify_tournament_results: '토너먼트 결과 입력/수정',
};

export const AUDIT_TARGET_LABEL: Record<string, string> = {
  buy_in_transaction: '바이인',
  prize_transaction: '상금',
  bonus_transaction: '보너스',
  tournament: '토너먼트',
};
